import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  length?: number;
  width?: number;
  height?: number;
}

// Rectangular prism that can "unfold" into its 6-face net.
//
// Design notes:
// - 3D projection uses a consistent 30° oblique angle so dimensions read clearly
//   regardless of how thin the prism is.
// - Each visible face is labelled with its own dimension numbers.
// - The net is drawn as a correct cross layout with each rectangle sized to
//   match its face (top/bottom = L×W, front/back = L×H, left/right = W×H).
export default function PrismViz({ length = 4, width = 3, height = 2 }: Props) {
  const [unfolded, setUnfolded] = useState(false);

  // Scale so the largest dimension maps to ~120px — readable on mobile, not oversized.
  const maxDim = Math.max(length, width, height);
  const unit = Math.max(24, Math.min(60, Math.floor(240 / maxDim)));

  const L = length * unit;
  const W = width * unit;
  const H = height * unit;

  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">
          Prism{' '}
          <span className="text-ink/50 text-sm font-body font-normal math-display">
            {length} × {width} × {height}
          </span>
        </h3>
        <button
          onClick={() => setUnfolded((v) => !v)}
          className="text-sm px-3 py-1.5 rounded-full bg-violet/15 text-violet font-semibold"
        >
          {unfolded ? 'Fold back' : 'Unfold it'}
        </button>
      </div>
      <div className="flex justify-center overflow-x-auto">
        <AnimatePresence mode="wait">
          {!unfolded ? <Prism3D key="3d" L={L} W={W} H={H} length={length} width={width} height={height} /> : <PrismNet key="net" L={L} W={W} H={H} length={length} width={width} height={height} />}
        </AnimatePresence>
      </div>
    </div>
  );
}

// --- 3D projection: front face + oblique depth ---

function Prism3D({
  L,
  W,
  H,
  length,
  width,
  height,
}: {
  L: number;
  W: number;
  H: number;
  length: number;
  width: number;
  height: number;
}) {
  // Oblique projection at 30° with 65% depth foreshortening.
  // cos(30°) ≈ 0.866, sin(30°) ≈ 0.5 — but we use 0.7/0.6 for a cleaner look.
  const depthX = W * 0.65;
  const depthY = W * 0.55;

  const padL = 40;
  const padT = depthY + 20;
  const svgW = L + depthX + padL + 20;
  const svgH = H + padT + 30;

  // Face polygon vertex builder (origin at front-face bottom-left).
  const front = `0,0 ${L},0 ${L},${-H} 0,${-H}`;
  const right = `${L},0 ${L + depthX},${-depthY} ${L + depthX},${-depthY - H} ${L},${-H}`;
  const top = `0,${-H} ${L},${-H} ${L + depthX},${-depthY - H} ${depthX},${-depthY - H}`;

  return (
    <motion.svg
      width="100%"
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ maxWidth: svgW, height: 'auto' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <g transform={`translate(${padL} ${padT + H})`}>
        {/* Top face (lightest) */}
        <polygon points={top} fill="#e8dcff" stroke="#1b2a3a" strokeWidth="1" />
        {/* Right face (darker) */}
        <polygon points={right} fill="#8b6ed6" stroke="#1b2a3a" strokeWidth="1" />
        {/* Front face (medium) */}
        <polygon points={front} fill="#b693f6" stroke="#1b2a3a" strokeWidth="1" />

        {/* Dimension labels on the visible faces */}
        {/* Length along front bottom edge */}
        <text x={L / 2} y={22} fontSize="13" textAnchor="middle" fill="#6b7582" fontWeight="700">
          {length}
        </text>
        {/* Height up the front-left edge */}
        <text x={-14} y={-H / 2} fontSize="13" textAnchor="end" dominantBaseline="middle" fill="#6b7582" fontWeight="700">
          {height}
        </text>
        {/* Width label on the top face (slightly inside) */}
        <text
          x={L + depthX / 2 + 3}
          y={-H - depthY / 2 - 2}
          fontSize="13"
          textAnchor="middle"
          fill="#6b7582"
          fontWeight="700"
        >
          {width}
        </text>
      </g>
    </motion.svg>
  );
}

// --- Flat net: correct cross layout with 6 faces ---
//
// Layout (L=length, W=width/depth, H=height):
//
//                    ┌──── L ────┐
//                    │  TOP L×W  │ W
//                    └───────────┘
//       ┌── W ──┬──── L ────┬── W ──┬──── L ────┐
//       │ LEFT  │  FRONT    │ RIGHT │   BACK    │ H
//       │  W×H  │   L×H     │  W×H  │    L×H    │
//       └───────┴───────────┴───────┴───────────┘
//                    ┌───────────┐
//                    │ BOTTOM    │ W
//                    │  L×W      │
//                    └───────────┘
//
// Total SVG width  = 2L + 2W
// Total SVG height = 2W + H

function PrismNet({
  L,
  W,
  H,
  length,
  width,
  height,
}: {
  L: number;
  W: number;
  H: number;
  length: number;
  width: number;
  height: number;
}) {
  const pad = 12;
  const svgW = 2 * L + 2 * W + pad * 2;
  const svgH = 2 * W + H + pad * 2;

  // Front face anchors at (W, W) — left of it is left face, right is right face.
  const frontX = W;
  const frontY = W;

  return (
    <motion.svg
      width="100%"
      viewBox={`0 0 ${svgW} ${svgH}`}
      style={{ maxWidth: svgW, height: 'auto' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <g transform={`translate(${pad} ${pad})`}>
        {/* TOP (L × W) above front */}
        <rect x={frontX} y={0} width={L} height={W} fill="#e8dcff" stroke="#1b2a3a" />
        <FaceLabel cx={frontX + L / 2} cy={W / 2} label="top" />

        {/* LEFT (W × H) */}
        <rect x={0} y={frontY} width={W} height={H} fill="#b693f6" stroke="#1b2a3a" />
        <FaceLabel cx={W / 2} cy={frontY + H / 2} label="side" />

        {/* FRONT (L × H) */}
        <rect x={frontX} y={frontY} width={L} height={H} fill="#b693f6" stroke="#1b2a3a" />
        <FaceLabel cx={frontX + L / 2} cy={frontY + H / 2} label="front" />

        {/* RIGHT (W × H) */}
        <rect x={frontX + L} y={frontY} width={W} height={H} fill="#b693f6" stroke="#1b2a3a" />
        <FaceLabel cx={frontX + L + W / 2} cy={frontY + H / 2} label="side" />

        {/* BACK (L × H) */}
        <rect x={frontX + L + W} y={frontY} width={L} height={H} fill="#b693f6" stroke="#1b2a3a" />
        <FaceLabel cx={frontX + L + W + L / 2} cy={frontY + H / 2} label="back" />

        {/* BOTTOM (L × W) below front */}
        <rect x={frontX} y={frontY + H} width={L} height={W} fill="#e8dcff" stroke="#1b2a3a" />
        <FaceLabel cx={frontX + L / 2} cy={frontY + H + W / 2} label="bottom" />

        {/* Dimension labels along outer edges */}
        <text
          x={frontX + L / 2}
          y={-2}
          fontSize="11"
          textAnchor="middle"
          fill="#6b7582"
          fontWeight="700"
        >
          {length}
        </text>
        <text
          x={-2}
          y={frontY + H / 2}
          fontSize="11"
          textAnchor="end"
          dominantBaseline="middle"
          fill="#6b7582"
          fontWeight="700"
        >
          {height}
        </text>
        <text
          x={frontX + L + W / 2}
          y={-2}
          fontSize="11"
          textAnchor="middle"
          fill="#6b7582"
          fontWeight="700"
        >
          {width}
        </text>
      </g>
    </motion.svg>
  );
}

function FaceLabel({ cx, cy, label }: { cx: number; cy: number; label: string }) {
  return (
    <text
      x={cx}
      y={cy}
      fontSize="10"
      textAnchor="middle"
      dominantBaseline="middle"
      fill="#1b2a3a"
      opacity={0.55}
      fontWeight="600"
    >
      {label}
    </text>
  );
}
